<template>
  <v-main
    v-if="!provincesLoader"
    class="home-page"
  >
    <HomeHero
      class="mb-12"
      :show-scroll-down="!currentProvinceName"
    />
    <v-container
      v-if="bubblesLoader !== null"
      id="list"
      class="pt-0"
    >
      <v-row
        v-if="bubblesLoader === false && bubblesList?.length > 0"
        no-gutters
      >
        <v-col
          cols="12"
          class="mb-12 sz-primary-background py-4"
        >
          <h2 class="text-h3 text-sm-h2 font-weight-medium sz-primary-grey--text text-center">
            {{ $t('home.province') }} {{ currentProvinceName }}
          </h2>
          <p class="f-18 font-weight-medium sz-primary-grey--text text-center mb-0">
            {{ $t('home.choose_where') }}
          </p>
        </v-col>
        <v-col
          v-if="difficultBubbles.length > 0"
          cols="12"
          class="mb-12"
        >
          <v-row no-gutters>
            <v-col cols="12">
              <h3 class="text-h3 text-md-h2 font-weight-medium sz-primary-grey--text mb-2">
                {{ $t('home.missing_donors') }}
              </h3>
              <p class="f-16 font-weight-bold sz-secondary-grey--text mb-0">
                {{ $t('home.missing_donors_details') }}
              </p>
            </v-col>
            <v-col cols="12">
              <v-row no-gutters>
                <v-col
                  v-for="(bubble, index) in difficultBubbles"
                  :key="index"
                  cols="12"
                  sm="6"
                  class="d-flex justify-center"
                  :class="[(index % 2 === 0) ? 'pr-sm-3' : 'pl-sm-3', 'mt-5']"
                >
                  <SzBubble
                    :name="bubble.name"
                    :link="getFamilyListRoute(bubble)"
                    :highlighted="true"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          v-if="difficultBubbles.length > 0 && otherBubbles.length > 0"
          cols="12"
        >
          <v-divider class="mb-12 d-block" />
        </v-col>
        <v-col
          v-if="otherBubbles.length > 0"
          cols="12"
          class="mb-12"
        >
          <v-row no-gutters>
            <v-col
              v-if="difficultBubbles.length > 0"
              cols="12"
            >
              <h3 class="text-h3 text-md-h2 font-weight-medium sz-primary-grey--text mb-0">
                {{ $t('home.other_locations') }}
              </h3>
            </v-col>
            <v-col
              cols="12"
              :class="{ 'mt-n5': difficultBubbles.length <= 0 }"
            >
              <v-row no-gutters>
                <v-col
                  v-for="(bubble, index) in otherBubbles"
                  :key="index"
                  cols="12"
                  sm="6"
                  class="d-flex justify-center"
                  :class="[(index % 2 === 0) ? 'pr-sm-3' : 'pl-sm-3', 'mt-5']"
                >
                  <SzBubble
                    :name="bubble.name"
                    :link="getFamilyListRoute(bubble)"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
        <InfoBox
          :title="$t('home.location_not_found')"
          :text="$t('home.location_not_found_details')"
        />
      </v-row>
      <v-row
        v-else-if="bubblesLoader === true"
        class="mb-12"
      >
        <LoaderComponent />
      </v-row>
    </v-container>
    <v-container
      id="firstInfoContainer"
      class="py-0"
    >
      <v-row no-gutters>
        <BannerBox v-bind="$tm('banners.APHomeBannerTop')" />
        <DonorSteps
          v-if="!currentProvinceName"
          :title="t('home.homeSteps.title')"
          :steps="tm('home.homeSteps.steps')"
          :guide-button-text="$t('label.showGuide')"
        />
      </v-row>
    </v-container>
    <v-container
      v-if="!currentProvinceName"
      fluid
      class="pa-0"
    >
      <section>
        <v-row no-gutters>
          <SlidersContainer class="mb-12" />
        </v-row>
      </section>
    </v-container>
    <v-container v-if="!currentProvinceName">
      <FAQPanels :faq="homePageFaq">
        <div class="mt-6 d-flex justify-space-evenly flex-wrap align-end">
          <BaseButton
            :href="LINKS.guide"
            variant="secondary"
            class="home-page__faq-button mt-3 order-1 order-sm-0"
          >
            {{
              $t('home.faq.moreInfo') }}
          </BaseButton>
          <BaseButton
            v-if="isCampaignUnderway"
            class="home-page__faq-button"
            @click="scrollTop"
          >
            {{ $t('home.faq.choosingFamily') }}
          </BaseButton>
          <BaseButton
            v-if="isAfterCampaign || isBeforeCampaign"
            :href="LINKS.szpHomePage"
            class="home-page__faq-button"
          >
            {{ $t('label.goToHomePage') }}
          </BaseButton>
        </div>
      </FAQPanels>
    </v-container>
    <v-container
      v-if="!currentProvinceName"
      class="mb-5"
    >
      <InfoSectionHome v-if="isCampaignUnderway" />
    </v-container>
    <v-container
      v-if="!currentProvinceName"
      class="py-0"
    >
      <v-row no-gutters>
        <div class="w-100">
          <BannerBox v-bind="$tm('banners.SzPHomeBanner')" />
          <BannerBox v-bind="$tm('banners.APHomeBannerBottom')" />
        </div>
        <SponsorsComponent class="mt-3 mb-5 w-100" />
      </v-row>
    </v-container>
    <ModalCustom
      v-model="passwordPopup"
      :closeable="false"
      :icon="false"
      :divider="false"
    >
      <template #header>
        <p class="mb-2 pt-5 sz-primary-grey--text f-24 font-weight-medium">
          {{ $t('home.passwordPopup') }}
        </p>
        <TextField
          v-model="password"
          type="password"
          :placeholder="$t('label.code')"
          rules="required"
          name="password"
          :validate-on-value-update="false"
          @keyup.enter="submitPassword"
          @input="hidePasswordErrorText"
        />
        <v-btn
          color="szRed"
          class="primary-button mt-3 mb-3"
          variant="flat"
          :block="$vuetify.display.xs"
          :disabled="submitPasswordButtonDisabled"
          @click="submitPassword"
        >
          {{ $t('label.ok') }}
        </v-btn>
        <p
          v-if="showPasswordErrorText"
          class="mb-0 d-inline-block ml-3 sz-red--text font-weight-medium f-16"
        >
          {{ $t('label.errorPassword') }}
        </p>
      </template>
    </ModalCustom>
    <ScrollTopButton />
    <FooterComponent class="mt-5" />
  </v-main>
  <LoaderComponent
    v-else
    page
  />
</template>

<script setup lang="ts">
import HomeHero from '@/components/Home/HomeHero.vue';
import InfoSectionHome from '@/components/Home/InfoSection/InfoSectionHome.vue';
import SzBubble from '@/components/Home/SzBubble.vue';
import BannerBox from '@/components/widgets/BannerBox/BannerBox.vue';
import BaseButton from '@/components/widgets/BaseButton.vue';
import DonorSteps from '@/components/widgets/DonorSteps.vue';
import FAQPanels from '@/components/widgets/FAQPanels.vue';
import FooterComponent from '@/components/widgets/FooterComponent.vue';
import InfoBox from '@/components/widgets/InfoBox.vue';
import TextField from '@/components/widgets/Inputs/TextField.vue';
import LoaderComponent from '@/components/widgets/LoaderComponent.vue';
import ModalCustom from '@/components/widgets/ModalCustom.vue';
import ScrollTopButton from '@/components/widgets/ScrollTopButton.vue';
import SlidersContainer from '@/components/widgets/SlidersContainer.vue';
import SponsorsComponent from '@/components/widgets/SponsorsComponent.vue';
import { LINKS } from '@/constants/externalLinks';
import { HOME_PAGE_FAQ, HOME_PAGE_MOBILE_FAQ } from '@/constants/faq';
import { createSelectedProvinceEvent } from '@/plugins/gtm/events';
import { useGtm } from '@gtm-support/vue-gtm';
import { isMobile } from 'is-mobile';
import { useForm } from 'vee-validate';
import { computed, nextTick, ref, watch } from 'vue';
import { useGoTo } from 'vuetify';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import useEditionStageStore from '@/stores/EditionStage';
import Http from '@/services/http';
import { useI18n } from 'vue-i18n';
import apiResource from '@/services/http/api/resource.ts';
import router from '@/router';
import ROUTE_NAMES from '@/constants/routeNames';

const goTo = useGoTo();
const store = useStore();
const gtm = useGtm();

const { t, tm } = useI18n();

store.commit('bubbles/clear');
store.commit('provinces/clear');

const editionStageStore = useEditionStageStore();

const {
    isBeforeCampaign,
    isAfterCampaign,
    isCampaignUnderway,
    passwordPopup
} = storeToRefs(editionStageStore);

const provincesLoader = computed(() => store.getters['provinces/getLoader']);

const bubblesList = computed(() => {
    const bubbles = store.getters['bubbles/getBubbles'] ?? [];
    return bubbles.filter(bubble => !!bubble?.name && !!bubble?.bubble_id);
});

const otherBubbles = computed(() => bubblesList.value.filter(el => !el.is_difficult));

const currentProvinceName = computed(() => store.getters['provinces/getCurrentProvinceName']);

const homePageFaq = computed(() => isMobile() ? HOME_PAGE_MOBILE_FAQ : HOME_PAGE_FAQ);

const difficultBubbles = computed(() => bubblesList.value.filter(el => el.is_difficult));

const bubblesLoader = computed(() => store.getters['bubbles/getLoader']);

watch(bubblesLoader, async (val) => {
    if (val === false) {
        await nextTick();
        goTo('#list', {
            offset: 48,
            easing: 'easeInQuad'
        });
    }
});

watch(currentProvinceName, (val) => {
    if (gtm) gtm.trackEvent(createSelectedProvinceEvent(val));
});

function initProvinces () {
    if (isCampaignUnderway.value) {
        store.dispatch('provinces/getFamiliesAmount');
        store.dispatch('provinces/getProvinces');
    } else {
        store.commit('provinces/setLoader', false);
    }
}

initProvinces();

useForm();
const password = ref<string>('');
const showPasswordErrorText = ref<boolean>(false);
const submitPasswordButtonDisabled = ref<boolean>(false);
const securePasswordValid = ref<boolean>(false);

const submitPassword = (): void => {
    submitPasswordButtonDisabled.value = true;
    Http.addSecureHeader(password.value);

    apiResource.getProvinces().then(() => {
        securePasswordValid.value = true;
    }).catch(() => {

    }).finally(() => {
        submitPasswordButtonDisabled.value = false;
    });
};
const hidePasswordErrorText = (): void => {
    showPasswordErrorText.value = false;
};
if (passwordPopup) {
    watch(securePasswordValid, (isSecurePasswordValid) => {
        if (isSecurePasswordValid) {
            editionStageStore.setEditionStageOpen();
            initProvinces();
            passwordPopup.value = false;
            router.push({
                name: ROUTE_NAMES.HOME
            });
        } else {
            showPasswordErrorText.value = true;
            submitPasswordButtonDisabled.value = false;
        }
    });
}

function getFamilyListRoute (bubble) {
    return {
        name: ROUTE_NAMES.FAMILIES_LIST,
        params: {
            bubbleId: bubble?.bubble_id,
            ga: {
                familyWarehouse: bubble?.name,
                provinceId: bubble?.province?.province_id,
                provinceName: bubble?.province?.name
            }
        }
    };
}

function scrollTop () {
    goTo('body', {
        easing: 'easeInQuad'
    });
}

</script>
<style lang="scss">
.home-page {
  &__faq-button {
    width: 100%;
    max-width: 300px;
  }
}
</style>
